import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, LogOut, PanelRightOpen, Search, Settings as SettingsIcon, Sparkles } from "lucide-react";
import { Logo } from "./Logo";
import { LeftRail } from "./LeftRail";
import { Workspace } from "@/features/workspace/Workspace";
import { InsightsPanel } from "@/components/workspace/InsightsPanel";
import { CommandPalette, openCommandPalette } from "@/components/ui/command-palette";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useSession } from "@/stores/session";
import { useWorkspace, type WorkspaceView } from "@/stores/workspace";
import { firebaseSignOut } from "@/lib/firebase";
import { Landing } from "@/features/landing/Landing";
import { cn } from "@/lib/cn";

const RAIL_KEY = "gslm-rail-expanded";

const VIEW_TITLE: Record<WorkspaceView, string> = {
  learn: "Learn",
  dashboard: "Dashboard",
  tutor: "Tutor",
  practice: "Practice",
  exam: "Mock exams",
  topics: "Topics",
  flashcards: "Flashcards",
  score: "Score",
  resources: "Library",
  settings: "Settings",
};

export function AppShell() {
  const { user, signOut } = useSession();
  const view = useWorkspace((s) => s.view);
  const setView = useWorkspace((s) => s.setView);
  const insightsOpen = useWorkspace((s) => s.insightsOpen);
  const setInsightsOpen = useWorkspace((s) => s.setInsightsOpen);
  const nav = useNavigate();
  const mainRef = useRef<HTMLElement>(null);
  const [railExpanded, setRailExpanded] = useState(
    () => localStorage.getItem(RAIL_KEY) !== "false",
  );
  const [menuOpen, setMenuOpen] = useState(false);

  const doSignOut = () => {
    firebaseSignOut().catch(() => undefined);
    signOut();
    nav("/");
  };

  useEffect(() => {
    localStorage.setItem(RAIL_KEY, String(railExpanded));
  }, [railExpanded]);
  // Reset scroll to top whenever the active tool changes.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, behavior: "auto" });
  }, [view]);

  // Signed out → animated capabilities page (with sign-in). Signed in → workspace.
  if (!user) return <Landing />;

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background">
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 bg-editorial-fade" />

      {/* Slim top strip */}
      <header className="flex h-14 shrink-0 items-center justify-between gap-4 border-b border-border/60 bg-background/70 px-4 backdrop-blur-xl">
        <div className="flex shrink-0 items-center gap-3">
          <button type="button" onClick={() => setView("learn")}>
            <Logo showWordmark={false} />
          </button>
          <div className="hidden items-center gap-2 sm:flex">
            <span className="font-serif text-base tracking-tight">Think Big Techies</span>
            <span className="text-muted-foreground/40">/</span>
            <span className="text-sm text-muted-foreground">{VIEW_TITLE[view]}</span>
          </div>
        </div>

        <button
          type="button"
          onClick={openCommandPalette}
          className="flex max-w-md flex-1 items-center gap-2 rounded-full border border-border/60 bg-card/60 px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          <Search className="size-3.5" />
          <span className="flex-1 text-left">Search or jump to…</span>
          <kbd className="rounded border border-border/70 bg-background/70 px-1.5 py-0.5 font-mono text-[10px]">
            ⌘K
          </kbd>
        </button>

        <div className="flex items-center gap-1.5">
          {!insightsOpen && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setInsightsOpen(true)}
              className="gap-1.5"
            >
              <Sparkles className="size-3.5" /> Ask Maya
            </Button>
          )}
          <ThemeToggle compact />
          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              className="flex items-center gap-1.5 rounded-full border border-border/60 bg-card/60 py-1 pl-1 pr-2 transition-colors hover:bg-secondary/60"
              aria-label="Account menu"
            >
              <Avatar className="size-7">
                <AvatarFallback className="bg-foreground text-[11px] text-background">
                  {user.name?.[0]?.toUpperCase() ?? "S"}
                </AvatarFallback>
              </Avatar>
              <ChevronDown className={cn("size-3.5 text-muted-foreground transition-transform", menuOpen && "rotate-180")} />
            </button>
            <AnimatePresence>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: -6, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.98 }}
                    transition={{ duration: 0.14 }}
                    className="absolute right-0 top-11 z-50 w-60 overflow-hidden rounded-2xl border border-border/60 bg-card p-1.5 shadow-editorial-lg"
                  >
                    <div className="border-b border-border/60 px-3 py-2">
                      <div className="truncate text-sm font-medium">{user.name}</div>
                      <div className="truncate text-xs text-muted-foreground">{user.email}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setView("settings"); setMenuOpen(false); }}
                      className="mt-1 flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-sm transition-colors hover:bg-secondary/60"
                    >
                      <SettingsIcon className="size-4 text-muted-foreground" /> Settings
                    </button>
                    <button
                      type="button"
                      onClick={doSignOut}
                      className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-sm text-destructive transition-colors hover:bg-destructive/10"
                    >
                      <LogOut className="size-4" /> Sign out
                    </button>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </header>

      {/* Three-panel body */}
      <div className="flex min-h-0 flex-1">
        <LeftRail
          expanded={railExpanded}
          onToggle={() => setRailExpanded((v) => !v)}
        />

        <main ref={mainRef} className="min-w-0 flex-1 overflow-y-auto">
          <Workspace />
        </main>

        {/* Right insights panel — desktop inline, mobile overlay */}
        <AnimatePresence>
          {insightsOpen && (
            <motion.aside
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 384, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ type: "spring", stiffness: 380, damping: 40 }}
              className="hidden shrink-0 border-l border-border/60 lg:block"
            >
              <div className="h-full w-96">
                <InsightsPanel onClose={() => setInsightsOpen(false)} />
              </div>
            </motion.aside>
          )}
        </AnimatePresence>
      </div>

      {/* Mobile insights overlay */}
      <AnimatePresence>
        {insightsOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 lg:hidden"
          >
            <div
              className="absolute inset-0 bg-foreground/20 backdrop-blur-sm"
              onClick={() => setInsightsOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 380, damping: 40 }}
              className="absolute inset-y-0 right-0 w-[86%] max-w-sm border-l border-border/60"
            >
              <InsightsPanel onClose={() => setInsightsOpen(false)} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating open button when closed (mobile) */}
      {!insightsOpen && (
        <button
          type="button"
          onClick={() => setInsightsOpen(true)}
          className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-foreground px-4 py-3 text-sm font-medium text-background shadow-editorial-lg lg:hidden"
        >
          <PanelRightOpen className="size-4" /> Ask Maya
        </button>
      )}

      <CommandPalette />
    </div>
  );
}
